import * as SystemOfUnitsConstants from '@/lib/system-of-units-constants'
import store from '@/store'
import numeral from 'numeral'
import convert from 'convert-units'

export const FORMAT_SINGLE_ZERO = '0'
export const FORMAT_DOUBLE_ZERO = '0.0'
export const FORMAT_TRIPLE_ZERO = '0.00'
export const FORMAT_QUAD_ZERO = '0.000'
const SPACE = ' '

const InvalidSpeed = '-.-'

// Length
// Metric
const MeterUnit = 'm'
const KiloMeterUnit = 'km'
// Imperial
const MileUnit = 'mi'
const FeetUnit = 'ft'
const InchUnit = 'in'

// Area
// Metric
const SquareMeterUnit = 'm2'
const HectareUnit = 'ha'
// Imperial
const AcreUnit = 'ac'
// Japanese
const JpTanUnit = '反'
const JpChouUnit = '町'
const JpChouAsSqMeter = 9917.36
const JpTanAsSqMeter = 991.736
// Thai
const ThRaiUnit = 'rai'
const ThRaiAsSqMeter = 1600

export function getSpeedTextFrom(meterPerSecond) {
  if (isMetric()) {
    if (meterPerSecond) {
      const speed = convert(meterPerSecond)
        .from(SystemOfUnitsConstants.MeterPerSecond)
        .to(SystemOfUnitsConstants.KilloMeterPerHour)
      return numeral(speed).format(FORMAT_DOUBLE_ZERO) + SPACE + SystemOfUnitsConstants.KilloMeterPerHour
    }
    return InvalidSpeed + SystemOfUnitsConstants.KilloMeterPerHour
  }
  if (isImperial()) {
    if (meterPerSecond) {
      // convertはmphに対応していない
      const speed = (meterPerSecond * 3600) / 1609.344
      return numeral(speed).format(FORMAT_DOUBLE_ZERO) + SPACE + SystemOfUnitsConstants.MilePerHour
    }
    return InvalidSpeed + SystemOfUnitsConstants.MilePerHour
  }
  return ''
}

export function getDistanceTextFrom(distanceAsMeter) {
  if (isMetric()) {
    if (distanceAsMeter < 1000) {
      return numeral(distanceAsMeter).format(FORMAT_SINGLE_ZERO) + SPACE + MeterUnit
    } else {
      const distance = convert(distanceAsMeter)
        .from(MeterUnit)
        .to(KiloMeterUnit)
      return numeral(distance).format(FORMAT_DOUBLE_ZERO) + SPACE + KiloMeterUnit
    }
  }
  if (isImperial()) {
    const distance = convert(distanceAsMeter)
      .from(MeterUnit)
      .to(MileUnit)
    return numeral(distance).format(FORMAT_DOUBLE_ZERO) + SPACE + MileUnit
  }
  return ''
}

export function getAreaTextFrom(areaAsSqMeter, showWithSquareMeter = false) {
  let value = ''
  switch (store.getters.systemOfUnits) {
    case SystemOfUnitsConstants.Metric:
      const metricArea = convert(areaAsSqMeter)
        .from(SquareMeterUnit)
        .to(HectareUnit)
      value = numeral(metricArea).format(FORMAT_TRIPLE_ZERO) + SPACE + HectareUnit
      break
    case SystemOfUnitsConstants.Imperial:
      const imperialArea = convert(areaAsSqMeter)
        .from(SquareMeterUnit)
        .to(AcreUnit)
      value = numeral(imperialArea).format(FORMAT_DOUBLE_ZERO) + SPACE + AcreUnit
      break
    case SystemOfUnitsConstants.JpUnitsOfArea:
      if (JpChouAsSqMeter <= areaAsSqMeter) {
        const chou = areaAsSqMeter / JpChouAsSqMeter
        value = numeral(chou).format(FORMAT_TRIPLE_ZERO) + SPACE + JpChouUnit
      } else {
        const tan = areaAsSqMeter / JpTanAsSqMeter
        value = numeral(tan).format(FORMAT_DOUBLE_ZERO) + SPACE + JpTanUnit
      }
      break
    case SystemOfUnitsConstants.ThUnitsOfArea:
      const rai = areaAsSqMeter / ThRaiAsSqMeter
      value = numeral(rai).format(FORMAT_DOUBLE_ZERO) + SPACE + ThRaiUnit
      break
    default:
      break
  }
  if (showWithSquareMeter) {
    value = value + SPACE + '(' + numeral(areaAsSqMeter).format(FORMAT_DOUBLE_ZERO) + SPACE + MeterUnit + '&sup2;)'
  }
  return value
}

export function getLengthTextFrom(lengthAsMeter, options = { format: FORMAT_SINGLE_ZERO }) {
  if (isMetric()) {
    return numeral(lengthAsMeter).format(options.format) + SPACE + MeterUnit
  } else {
    const length = convert(lengthAsMeter)
      .from(MeterUnit)
      .to(FeetUnit)
    return numeral(length).format(options.format) + SPACE + FeetUnit
  }
}

export function getWindSpeedTextFrom(meterPerSecond) {
  if (isMetric()) {
    if (meterPerSecond) {
      return numeral(meterPerSecond).format(FORMAT_DOUBLE_ZERO) + SPACE + SystemOfUnitsConstants.MeterPerSecond
    }
    return InvalidSpeed + SystemOfUnitsConstants.MeterPerSecond
  }
  if (isImperial()) {
    if (meterPerSecond) {
      // convertはmphに対応していない
      const speed = (meterPerSecond * 3600) / 1609.344
      return numeral(speed).format(FORMAT_DOUBLE_ZERO) + SPACE + SystemOfUnitsConstants.MilePerHour
    }
    return InvalidSpeed + SystemOfUnitsConstants.MilePerHour
  }
  return ''
}

export function getTemperatureUnit() {
  if (isMetric()) {
    return SystemOfUnitsConstants.Celsius
  }
  if (isImperial()) {
    return SystemOfUnitsConstants.Fahrenheit
  }
  return ''
}

export function getTemperatureTextFrom(celsiusTemperature, digits) {
  let f = undefined
  switch (digits) {
    case 1:
      f = FORMAT_SINGLE_ZERO
      break
    case 2:
      f = FORMAT_DOUBLE_ZERO
      break
    case 3:
      f = FORMAT_TRIPLE_ZERO
      break
    default:
      f = FORMAT_DOUBLE_ZERO
  }
  if (isMetric()) {
    return numeral(celsiusTemperature).format(f) + SPACE + SystemOfUnitsConstants.Celsius
  }
  if (isImperial()) {
    const temp = getFahrenheitValueFromCelsius(celsiusTemperature)
    return numeral(temp).format(f) + SPACE + SystemOfUnitsConstants.Fahrenheit
  }
}

export function getFahrenheitValueFromCelsius(celsiusValue) {
  return convert(celsiusValue)
    .from('C')
    .to('F')
}

export function getCelsiusValueFromFahrenheit(fahrenheitValue) {
  return convert(fahrenheitValue)
    .from('F')
    .to('C')
}

export function getPercent(percent) {
  return percent + SystemOfUnitsConstants.Percent
}

export function getMilliMetres(milliMetres) {
  return milliMetres + SystemOfUnitsConstants.MilliMetres
}

export function isMetric() {
  return store.getters.systemOfUnits === SystemOfUnitsConstants.Metric
}

export function isImperial() {
  return store.getters.systemOfUnits === SystemOfUnitsConstants.Imperial
}

export function getCurrentSystemOfUnits() {
  return store.getters.systemOfUnits
}

export function toMeterPerSecFromKiloPerHour(value) {
  return convert(value)
    .from('km/h')
    .to('m/s')
}
